import { t } from '@lingui/macro';
import classnames from 'classnames';
import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import {
    onChangeOsmAdminBoundariesLevelsFilter,
    onChangeOsmAdminBoundariesShowLabels,
    updateOsmAdminBoundariesAvailableLevels,
} from '../../../ac/onChangeMapFilters';
import { DYNAMIC_FILTERS_CONFIG } from '../../../constants/dynamicFiltersConfig';
import { withDisplayedLayersCheck } from '../../../HOC/withDisplayedLayersCheck';
import { DynamicAttributeFilter } from '../DynamicAttributeFilter';
import styles from '../FilterSidebar.module.css';

/**
 * Уровни административного деления OSM.
 * Цвет единый (#a78bfa), толщина визуально убывает с ростом уровня — это отражено в layerStyles.
 */
const { fallbackValues } = DYNAMIC_FILTERS_CONFIG.OSM_ADMIN_BOUNDARIES_LEVELS;
// Соответствует интерполяции line-width в layers.js
const LEVEL_LINE_WIDTH = { 4: 4.0, 5: 3.5, 6: 2.5, 7: 2.0, 8: 1.5, 9: 1.1, 10: 0.8, 11: 0.6, 14: 0.4 };
const LEVEL_COLOR = {
    4:  '#ffffff',
    5:  '#ffffaa',
    6:  '#ffee55',
    7:  '#ffcc00',
    8:  '#ff9900',
    9:  '#ff6600',
    10: '#ff3300',
    11: '#ff0000',
    14: '#cc0000',
};
const ADMIN_LEVEL_BASE_TYPES = fallbackValues.map(level => ({
    value: level,
    fill: LEVEL_COLOR[Number(level)] || '#ffffff',
}));
const renderAdminLevelIcon = ({ value, fill }) => {
    const strokeWidth = Math.max(LEVEL_LINE_WIDTH[Number(value)] || 1, 0.4) * 3;
    return (
        <svg
            width='2vw'
            height='2vw'
            viewBox='0 0 24 24'
            style={{ minWidth: 16, minHeight: 16 }}
        >
            <line
                x1='2'
                y1='12'
                x2='22'
                y2='12'
                stroke={fill}
                strokeWidth={strokeWidth}
                strokeLinecap='round'
                opacity='0.8'
            />
        </svg>
    );
};
const OsmAdminBoundariesFilterComponent = ({
    mapFilters,
    onChangeOsmAdminBoundariesLevelsFilter,
    onChangeOsmAdminBoundariesShowLabels,
}) => {
    const { osmAdminBoundariesAvailableLevels, osmAdminBoundariesLevelsDisabled, osmAdminBoundariesShowLabels } = mapFilters;
    const adminLevelTypes = useMemo(() => ADMIN_LEVEL_BASE_TYPES.map(item => ({
        ...item,
        label: `${t`Уровень`} ${item.value}`,
    })), []);

    return (
        <div>
            <DynamicAttributeFilter
                title={t`Условные уровни административного деления`}
                attributeTypes={adminLevelTypes}
                availableValues={osmAdminBoundariesAvailableLevels}
                disabledValues={osmAdminBoundariesLevelsDisabled}
                onToggle={onChangeOsmAdminBoundariesLevelsFilter}
                inputNamePrefix="osm-admin-level"
                renderIcon={renderAdminLevelIcon}
            />
            <br />
            <div className={styles.checkboxIconGrid}>
                <span />
                <label htmlFor="osm-admin-labels">{t`Названия административных единиц`}</label>
                <div>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            id="osm-admin-labels"
                            checked={osmAdminBoundariesShowLabels}
                            onChange={onChangeOsmAdminBoundariesShowLabels}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>
            </div>
            <div className={styles.dataDescription}>
                {t`На основе данных и классификации`}{' '}
                <a
                    href="https://wiki.openstreetmap.org/wiki/Tag:boundary%3Dadministrative#Table_:_Admin_level_for_all_countries"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#ffffff' }}
                >
                    {'OpenStreetMap'}
                </a>
            </div>
        </div>
    );
};
const mapStateToProps = ({ mapFilters }) => ({ mapFilters });
const ConnectedComponent = connect(
    mapStateToProps,
    {
        onChangeOsmAdminBoundariesLevelsFilter,
        onChangeOsmAdminBoundariesShowLabels,
        updateOsmAdminBoundariesAvailableLevels,
    }
)(OsmAdminBoundariesFilterComponent);

export const OsmAdminBoundariesFilter = withDisplayedLayersCheck(
    ConnectedComponent,
    t`Административное деление`
);
